import { NgModule, CUSTOM_ELEMENTS_SCHEMA, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { RouteReuseStrategy } from '@angular/router';
import { AppRoutingModule } from './app-routing.module';




// MSAL imports
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent } from '@azure/msal-angular';

import { AppComponent } from './app.component';
import { environment } from '../environments/environment';
import { FormLayoutComponent } from './components/forms/form-submission/form-layout.component';
import { FormsLandingComponent } from './components/landing-page/logged-in-landing/forms-landing.component';
import { LandingPageComponent } from './components/landing-page/logged-out-landing/landing-page.component';
import { NearMissReportComponent } from './components/forms/form-submission/individual-forms/near-miss-report/near-miss-report.component';
import { WeeklySiteInspectionComponent } from './components/forms/form-submission/individual-forms/weekly-site-inspection/weekly-site-inspection.component';
import { SupervisorInjuryReportComponent } from './components/forms/form-submission/individual-forms/supervisor-injury-report/supervisor-injury-report.component';
import { PreTaskSafetyPlanComponent } from './components/forms/form-submission/individual-forms/pre-task-safety-plan/pre-task-safety-plan.component';
import { PfizerPreTaskSafetyPlanComponent } from './components/forms/form-submission/individual-forms/pfizer-pre-task-safety-plan/pfizer-pre-task-safety-plan.component';
import { PfizerTemplateManagerModalComponent } from './components/forms/form-submission/individual-forms/pfizer-pre-task-safety-plan/pfizer-template-manager-modal/pfizer-template-manager-modal.component';
import { HotWorkPermitComponent } from './components/forms/form-submission/individual-forms/hot-work-permit/hot-work-permit.component';
import { FallProtectionPlanComponent } from './components/forms/form-submission/individual-forms/fall-protection-plan/fall-protection-plan.component';
import { EnergizedElecWorkPermitComponent } from './components/forms/form-submission/individual-forms/energized-elec-work-permit/energized-elec-work-permit.component';
import { EmployeeInjuryReportComponent } from './components/forms/form-submission/individual-forms/employee-injury-report/employee-injury-report.component';
import { ForkliftInspectionComponent } from './components/forms/form-submission/individual-forms/forklift-inspection/forklift-inspection.component';
import { AerialAndScissorLiftComponent } from './components/forms/form-submission/individual-forms/aerial-and-scissor-lift/aerial-and-scissor-lift.component';
import { ConfinedSpaceAltEntryComponent } from './components/forms/form-submission/individual-forms/confined-space-alt-entry/confined-space-alt-entry.component';
import { JobSafetyAuditComponent } from './components/forms/form-submission/individual-forms/job-safety-audit/job-safety-audit.component';
import { VoluntaryRespiratorUseComponent } from './components/forms/form-submission/individual-forms/volunteer-respirator/volunteer-respirator.component';
import { TrenchingExcavationWorkPlanComponent } from './components/forms/form-submission/individual-forms/trenching-excavation-work-plan/trenching-excavation-work-plan.component';

import { LoadAllIonicComponent } from './components/load-ionic-components/load-all-ionic.component';
import { AttachmentUploadSectionComponent } from './components/forms/form-submission/attachment-upload-section/attachment-upload-section.component';
import { AttachmentGridViewerComponent } from './components/forms/form-submission/attachment-upload-section/attachment-grid-viewer/attachment-grid-viewer.component';
import { AttachmentUploaderComponent } from './components/forms/form-submission/attachment-upload-section/attachment-uploader/attachment-uploader.component';
import { AttachmentViewerModalComponent } from './components/forms/form-submission/attachment-upload-section/attachment-viewer-modal/attachment-viewer-modal.component';
import { SignaturePadComponent } from './components/forms/form-submission/signature-section/signature-pad/signature-pad.component';
import { SignatureSectionComponent } from './components/forms/form-submission/signature-section/signature-section.component';
import { DynamicTableComponent } from './components/forms/form-entries/form-entry-list/entry-list/dynamic-table/dynamic-table.component';
import { MobileCardsComponent } from './components/forms/form-entries/form-entry-list/entry-list/mobile-cards/mobile-cards.component';
import { FormEntriesComponent } from './components/forms/form-entries/form-entry-list/form-entries.component';
import { MobileFilterModalComponent } from './components/forms/form-entries/form-entry-list/filter-entries/filter-entries.component';
import { FormViewComponent } from './components/forms/form-entries/form-entry-view/form-view.component';
import { BulkResendModalComponent } from './components/forms/form-entries/bulk-resend-modal/bulk-resend-modal.component';
import { FormRenderComponent } from './components/forms/shared/form-view-render/form-view-render.component';
import { EntraService } from './services/entra.service';

import { TextInputComponent } from './components/forms/form-submission/form-input-templates/text-input/text-input.component';
import { TextareaInputComponent } from './components/forms/form-submission/form-input-templates/textarea-input/textarea-input.component';
import { DateInputComponent } from './components/forms/form-submission/form-input-templates/date-input/date-input.component';
import { DateTimeInputComponent } from './components/forms/form-submission/form-input-templates/date-time-input/date-time-input.component';
import { RadioGroupInputComponent } from './components/forms/form-submission/form-input-templates/radio-group-input/radio-group-input.component';
import { SelectInputComponent } from './components/forms/form-submission/form-input-templates/select-input/select-input.component';
import { CheckboxInputComponent } from './components/forms/form-submission/form-input-templates/checkbox-input/checkbox-input.component';
import { SubmissionConfirmationComponent } from './components/forms/form-submission/submission-confirmation/form-submission-confirmation.component';
import { SuggestionsPopoverComponent } from './components/forms/form-submission/form-input-templates/textarea-input/suggestions-popover/suggestions-popover.component';
import { PfizerTemplateFormModalComponent } from './components/forms/form-submission/individual-forms/pfizer-pre-task-safety-plan/pfizer-template-form-modal/pfizer-template-form-modal.component';

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId,
      authority: environment.authority,
      redirectUri: environment.redirectUri,
      postLogoutRedirectUri: environment.postLogoutRedirectUri
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['User.Read', 'User.Read.All']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['User.Read', 'User.Read.All']
    }
  };
}

/**
 * Initializes MSAL authentication before app starts.
 * 
 * @param entraService The service for initializing MSAL authentication.
 * @returns {() => Promise<void>} A function that returns a promise that resolves when MSAL authentication is initialized or rejects on error.
 */
export function initializeAppMsal(entraService: EntraService) {
  return () => Promise.all([
    entraService.msalInstanceInit()
  ]);
}

@NgModule({
  declarations: [
    AppComponent,
    FormLayoutComponent,
    FormsLandingComponent,
    LandingPageComponent,
    WeeklySiteInspectionComponent,
    NearMissReportComponent,
    SupervisorInjuryReportComponent,
    PreTaskSafetyPlanComponent,
    PfizerPreTaskSafetyPlanComponent, 
    PfizerTemplateManagerModalComponent,
    PfizerTemplateFormModalComponent,
    HotWorkPermitComponent,
    FallProtectionPlanComponent,
    EnergizedElecWorkPermitComponent,
    EmployeeInjuryReportComponent,
    ForkliftInspectionComponent,
    AerialAndScissorLiftComponent,
    ConfinedSpaceAltEntryComponent,
    JobSafetyAuditComponent,
    VoluntaryRespiratorUseComponent,
    TrenchingExcavationWorkPlanComponent,

    LoadAllIonicComponent,
    AttachmentUploadSectionComponent,
    AttachmentGridViewerComponent,
    AttachmentUploaderComponent,
    AttachmentViewerModalComponent,
    SignaturePadComponent,
    SignatureSectionComponent,
    DynamicTableComponent,
    MobileCardsComponent,
    MobileFilterModalComponent,
    FormEntriesComponent,
    FormViewComponent,
    BulkResendModalComponent,
    TextInputComponent,
    TextareaInputComponent,
    DateInputComponent,
    DateTimeInputComponent,
    RadioGroupInputComponent,
    SelectInputComponent,
    CheckboxInputComponent,
    FormRenderComponent,
    SubmissionConfirmationComponent,
    SuggestionsPopoverComponent
  ],
  imports: [
    BrowserModule,
    CommonModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    IonicModule.forRoot(),
    AppRoutingModule,
    MsalModule,
  
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: RouteReuseStrategy,
      useClass: IonicRouteStrategy
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppMsal,
      deps: [EntraService], 
      multi: true
    }
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { } 